// ============================================================================
// ENTRÉES VALORISÉES — le pied de l'onglet « Prix & marge » (maquette l.3785-3831).
//
// ⚠️ CE N'EST PAS UN HISTORIQUE D'ACHAT, C'EST LA JUSTIFICATION D'UN CHIFFRE. Le prix d'achat affiché
// en haut de l'onglet est un prix MOYEN PONDÉRÉ : sans ce tableau, il tombe du ciel, et rien ne dit
// si une réception à prix aberrant l'a tiré. Le pied porte donc le calcul lui-même — quantité, valeur,
// et le quotient des deux — pour que le nombre du haut se vérifie à la main.
//
// ⚠️ L'ABSENCE D'ENTRÉES EST UNE INFORMATION, PAS UN VIDE. Sans prix d'achat, marge et coefficient
// restent N/C et le PMP n'est que partiellement estimé : l'état vide le DIT, au lieu de laisser croire
// à un tableau qui n'a pas fini de charger.
// ============================================================================
import { formatMoney } from '@orgazone/money-format';
import { Trans } from '@orgazone/ui-web';
import type { PurchaseEntrySet } from '../api';
import { rescale } from '@orgazone/core-money';
import { t } from '../i18n';

/** Gabarit de colonnes du tableau — relevé l.3796, partagé par l'en-tête, les lignes et le cumul. */
const COLONNES = '82px minmax(0, 1fr) 52px 92px 96px';
/** Hauteur de la zone défilante (l.3800) : ~7 lignes. Au-delà, c'est le tableau qui défile, pas la fiche. */
const HAUTEUR_LISTE = 152;

/** Un montant en échelle FINE rendu en euros — l'arrondi n'a lieu qu'ICI, jamais en base. */
const euroFin = (fine: number): string => formatMoney(rescale(fine, 4, 2), { thousandsSep: ' ' });

/**
 * ⚠️ IL NE LIT PLUS LUI-MÊME : la tuile « PMP réel » du haut de l'onglet affiche le MÊME chiffre, et
 * deux lectures de la même route afficheraient deux PMP différents le temps que la seconde réponde.
 * Une seule lecture, deux affichages — c'est l'onglet qui la porte.
 */
export function EntreesValorisees({ jeu, failure }: { jeu: PurchaseEntrySet | null; failure: string | null }) {
  return (
    <section style={{ minWidth: 0, marginTop: 30, paddingTop: 24, borderTop: '1.5px solid var(--ink-200)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Entrées valorisées')}</div>
          <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: 'var(--ink-500)' }}>
            
            {t('Chaque réception avec son prix unitaire — c’est ce qui compose le prix moyen pondéré.')}
          </p>
        </div>
      </div>

      {/* ⚠️ L'ÉCHEC PORTE SON MOTIF RÉEL et ne se déguise pas en « aucune entrée » : les deux mènent à un
          tableau vide à l'écran, mais l'un veut dire « rien n'a été reçu » et l'autre « je n'ai pas su
          lire ». Les confondre, c'est la façon type dont une liste ment (§23.0). */}
      {failure !== null
        ? <p role="alert" style={{ margin: 0, fontSize: 'var(--t-note)', color: 'var(--danger-ink)' }}>
            {t('Les entrées n’ont pas pu être lues : {failure}', { failure })}
          </p>
        : jeu === null
          ? <p style={{ margin: 0, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Lecture des entrées…')}</p>
          : jeu.rows.length === 0 ? <Vide /> : <Tableau jeu={jeu} />}
    </section>
  );
}

/** Le tableau et son pied de cumul. */
function Tableau({ jeu }: { jeu: PurchaseEntrySet }) {
  return (
    <div>
      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: COLONNES, gap: 10, padding: '10px 14px',
          background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)',
        }}>
          <Heading>{t('Date')}</Heading>
          <Heading>{t('Document')}</Heading>
          <Heading droite>{t('Qté')}</Heading>
          <Heading droite>{t('Prix unit.')}</Heading>
          <Heading droite>{t('Total')}</Heading>
        </div>

        <div style={{ maxHeight: HAUTEUR_LISTE, overflowY: 'auto', overflowX: 'hidden' }}>
          {jeu.rows.map((r) => (
            <div key={r.id} style={{
              display: 'grid', gridTemplateColumns: COLONNES, gap: 10, padding: '9px 14px',
              borderBottom: '1px solid var(--ink-100)', alignItems: 'center', fontSize: '12.5px',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
                {day(r.at)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-800)' }}>
                  {r.document}
                </div>
                {/* ⚠️ « Fournisseur inconnu » PLUTÔT QU'UNE LIGNE VIDE : `supplier_id` est nullable par
                    décision (les prix repris du legacy n'en portent pas), et un blanc se lirait comme
                    un défaut d'affichage plutôt que comme une donnée absente à la source. */}
                <div style={{
                  fontSize: 'var(--t-meta)', color: 'var(--ink-500)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{r.supplier ?? t('Fournisseur inconnu')}</div>
              </div>
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-700)' }}>{r.quantity}</div>
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-800)', whiteSpace: 'nowrap' }}>
                {euroFin(r.unitPriceFine)}
              </div>
              <div style={{
                textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                color: 'var(--ink-900)', whiteSpace: 'nowrap',
              }}>{euroFin(r.quantity * r.unitPriceFine)}</div>
            </div>
          ))}
        </div>

                {/* ⚠️ LE CUMUL PORTE SUR TOUTES LES ENTRÉES, pas sur les lignes visibles : c’est le serveur qui
            l’agrège. Un PMP recalculé sur la page rendue serait faux ET crédible.
            ⚠️ ET LE PMP N’EST PLUS LE QUOTIENT DE CES DEUX-LÀ (2026-08-23). La légende disait « PMP =
            valeur ÷ quantité » : c’était vrai quand le serveur divisait les totaux d’achat, ça ne l’est
            plus depuis qu’il rejoue le grand livre. Laisser la phrase aurait affiché un mensonge
            ARITHMÉTIQUE — trois nombres côte à côte dont le troisième n’est pas le rapport des deux
            premiers. La note sous le tableau explique la différence plutôt que de la masquer. */}
        <div style={{
          display: 'grid', gridTemplateColumns: COLONNES, gap: 10, padding: '11px 14px',
          background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)',
          alignItems: 'center', fontSize: '12.5px',
        }}>
          <div style={{ fontWeight: 700, color: 'var(--ink-900)' }}>{t('Cumul des achats')}</div>
          <div style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>{t('tout ce qui est entré, depuis toujours')}</div>
          <div style={{ textAlign: 'right', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
            {jeu.totalQuantity}
          </div>
          {/* Le PMP est le seul chiffre en teal de la rangée : c'est LUI que le haut de l'onglet affiche. */}
          <div style={{
            textAlign: 'right', fontWeight: 700, color: 'var(--teal-700)',
            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
          }}>{jeu.pmpFine === null ? 'N/C' : euroFin(jeu.pmpFine)}</div>
          <div style={{
            textAlign: 'right', fontWeight: 700, color: 'var(--ink-900)',
            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
          }}>{euroFin(jeu.totalValueFine)}</div>
        </div>
      </div>

      {/* ⚠️ POURQUOI LE PMP N'EST PAS LE QUOTIENT DU PIED, expliqué là où l'œil pose la question. Trois
          nombres côte à côte invitent à faire la division ; ne pas retomber dessus ferait soupçonner
          une erreur de calcul, alors que c'est une différence de NATURE. */}
      <div style={{ marginTop: 10, fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
        {/* ⚠️ UNE PHRASE NE SE COUPE PAS SUR SES BALISES. Écrite en morceaux — « Le cumul résume les »,
            « ; le PMP est le coût du » — elle arrive chez le traducteur en fragments dont l'ordre est
            imposé par le français, et aucune langue ne peut le changer. `<Trans>` porte la phrase
            ENTIÈRE et les marqueurs numérotés disent où la mise en forme reprend. */}
        <Trans k='Le cumul résume les <1>achats</1> ; le PMP est le coût du <2>stock détenu</2>'
          parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
        {/* ⚠️ LE COMPTE EST UNE INCISE, et la PONCTUATION qui la relie vit dans le code : une clé qui
            commencerait par « . » ou finirait par un tiret ne serait plus une phrase pour personne. */}
        {jeu.stockQty > 0 && <> {t('— {n} pièce en stock', { n: jeu.stockQty })}</>}
        {'. '}{t('Les deux diffèrent dès qu’une vente s’intercale entre deux prix d’achat : ce qui est sorti ne pèse plus sur ce qui reste.')}
        {/* ⚠️ CE QUI EST EXCLU SE DIT, AVEC SON NOMBRE. Une moyenne partielle qui se tait se lit comme
            une moyenne complète — et personne ne va chercher ce qui manque (§23.0, D7).
            ⚠️ L'ACCORD NE SE FAIT PLUS À LA MAIN : `{n} déclinaison … est exclue` / `… sont exclues`
            était un ternaire sur `> 1`, la règle FRANÇAISE, imposée à toutes les langues — et il
            livrait au traducteur des moitiés de mots (« s sont exclues », « été écartée »). La clé
            est COMPTÉE : le moteur choisit la forme avec `Intl.PluralRules`. */}
        {jeu.variantsWithoutCost > 0 && (
          <>
            {' '}<Trans k='<1>{n} déclinaison</1> en stock n’a jamais eu de prix d’achat : elle est exclue du calcul plutôt que comptée à zéro.'
              vars={{ n: jeu.variantsWithoutCost }} parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
          </>
        )}
        {jeu.variantsInconsistent > 0 && (
          <>
            {' '}<Trans k='<1>{n} déclinaison</1> au grand livre incohérent a été écartée : leur historique d’entrées et de sorties ne se recompose pas.'
              vars={{ n: jeu.variantsInconsistent }} parts={[(x) => <strong style={{ color: 'var(--danger-ink)' }}>{x}</strong>]} />
          </>
        )}
        {' '}<Trans k='PMP conservé en <1>sous-centime</1>, arrondi seulement à l’affichage. Un prix déjà appliqué à une vente est figé.'
          parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
        {/* ⚠️ LA TRONCATURE SE DIT. Le cumul reste juste (il est agrégé sur tout), mais les lignes
            affichées ne sont pas toutes : sans cette mention, on additionnerait la colonne à la main
            sans retomber sur le total, et c'est le total qu'on soupçonnerait. */}
        {jeu.truncated && (
          <>
            {t('Seules les {n} entrées les plus récentes sont listées ; le cumul, lui, porte sur la totalité.', { n: jeu.rows.length })}</>
        )}
      </div>
    </div>
  );
}

/** Ce que l'absence d'entrée COÛTE — relevé l.3830, mot pour mot. */
function Vide() {
  return (
    <div style={{
      padding: '30px 20px', textAlign: 'center',
      border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-sm)',
    }}>
      <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucune entrée valorisée')}</div>
      <div style={{ fontSize: '12.5px', color: 'var(--ink-500)', marginTop: 4, lineHeight: 1.5 }}>
        {/* ⚠️ LE `<br/>` DU DESSIN D'ORIGINE A DISPARU, et c'est voulu : il coupait la phrase à un
            point choisi pour le FRANÇAIS. Traduite, la coupure tombe au milieu d'un groupe — l'anglais
            est plus court, l’espagnol plus long. Dans un état vide centré, le retour naturel du
            conteneur s’adapte à chaque langue ; un saut figé n’adapte rien. */}
        <Trans k='Sans prix d’achat, marge et coefficient restent <1>N/C</1>, et le PMP est <2>partiellement estimé</2>.'
          parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} />
      </div>
    </div>
  );
}

function Heading({ children, droite }: { children: React.ReactNode; droite?: boolean }) {
  return (
    <div style={{
      fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
      textTransform: 'uppercase', color: 'var(--ink-500)', textAlign: droite ? 'right' : 'left',
    }}>{children}</div>
  );
}

/**
 * Une date d'entrée, en JJ/MM/AA.
 * ⚠️ ANNÉE SUR DEUX CHIFFRES, comme la maquette : la colonne fait 82 px et porte une police mono —
 * le format long y déborderait, et une date tronquée est pire qu'une date courte.
 */
function day(epochMs: number): string {
  if (!epochMs) return '—';
  const d = new Date(epochMs);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${p(d.getFullYear() % 100)}`;
}
